<template>
  <MRow :gutter="0" class="flex-1">
    <MCol :class="{ 'mb-12': isPortalLogin }">
      <FlotoCrudContainer
        ref="list"
        :fetch-fn="getConversations"
        :scrollable="false"
        :create-fn="addConversation"
        :update-fn="updateConversation"
        :delete-fn="deleteConversation"
        :paging="false"
        :show-no-data="false"
        prepend-new-item
        :per-page="5"
        default-sort="createdTime"
        class="conversaton-container"
      >
        <template
          v-slot:add-controls="{ refreshList, sortedColumn, applySort }"
        >
          <Component
            :is="stickySearch ? 'MAffix' : 'div'"
            :offset-top="offsetTop"
            @change="searchAffixed = $event"
          >
            <div
              v-if="!hideSearchBar"
              :class="{ 'pt-2': searchAffixed }"
              class="conversaton-container-header mr-1 mt-1"
            >
              <MRow v-if="!isEnabledSearch && !currentConversationType">
                <MCol class="flex items-center gap-2">
                  <MButton
                    v-if="searchable"
                    variant="primary"
                    class="mx-1 button-neutral-lighter rounded px-1 flex items-center"
                    @click="isEnabledSearch = !isEnabledSearch"
                  >
                    <MIcon name="search" size="md" class="text-light px-1" />
                  </MButton>
                  <MInput
                    v-if="createOptions.length && !disabled"
                    class="mx-1 rounded"
                    :placeholder="$t('start_a_conversation')"
                    readonly
                    @click="handleFocus"
                  />
                  <template v-if="!disabled" class="flex">
                    <MTooltip v-for="c in filteredCreateOptions" :key="c.type">
                      <template v-slot:trigger>
                        <MButton
                          :id="c.type"
                          tabindex="0"
                          variant="default"
                          :aria-label="c.name"
                          class="flex items-center justify-center rounded p-1"
                          :class="{
                            action: true,
                            'ml-1': true,
                            'text-success': c.type === 'solution',
                            'text-light': c.type === 'conclusion',
                            'text-light': c.type === 'diagnosis',
                          }"
                          @click="setAddConversationType(c)"
                        >
                          <MIcon :name="c.icon" size="lg" />
                          <div
                            v-if="c.type === 'solution' && solution.text"
                            class="check-badge"
                          >
                            <MIcon name="check" size="xs" class="text-white" />
                          </div>
                        </MButton>
                      </template>
                      <span>{{ c.name }}</span>
                    </MTooltip>
                  </template>
                </MCol>
              </MRow>
              <MRow v-if="isEnabledSearch && !currentConversationType">
                <MCol v-if="searchable">
                  <Searchbox
                    v-model="filters"
                    :available-types="filterTypeOptions"
                    :sort-by="sortedColumn"
                    :apply-sort="applySort"
                    :apply-added-by-filter="applyAddedByFilter"
                    @close="isEnabledSearch = false"
                    @change="refreshList"
                  />
                </MCol>
              </MRow>
              <!-- <MDivider v-if="searchable && !currentConversationType" /> -->
            </div>
          </Component>
        </template>
        <template v-slot:form="{ submit }">
          <Transition name="slideBottom" :duration="{ leave: 0 }" mode="out-in">
            <div ref="addConversationContainerRef">
              <MRow
                v-if="currentConversationType"
                :key="currentConversationType.type"
              >
                <MCol>
                  <AddBox
                    :resource="resource"
                    :processing="processing"
                    :conversation-type="currentConversationType"
                    :is-private="isAddingPrivateConversation"
                    v-bind="currentConversation"
                    :allow-templates="allowTemplates"
                    :allow-subject="allowSubject"
                    :allow-email="allowEmail"
                    :allow-cc-email="allowCcEmail"
                    :allow-outgoing-email="allowOutgoingEmail"
                    :module-name="moduleName"
                    :allow-knowledges="allowInsertknowledges"
                    :rich-box-rows="richBoxRows"
                    :latest-replay="latestReplay"
                    :forward-defalut-content="forwardDefalutContent"
                    :show-note-as-first-response="showNoteAsFirstResponse"
                    :allow-save-in-draft="conversationCanSaveInDraft"
                    :default-email-send-config-id="defaultOutgoingEmail.id"
                    @update="updateConversation"
                    @submit="($event) => submit($event)"
                    @remove="currentConversationType = null"
                  >
                    <template
                      v-if="startedFromInput && createOptions.length > 1"
                      v-slot:editor-actions
                    >
                      <div class="editor-actions-container">
                        <div>
                          <MTooltip v-for="c in createOptions" :key="c.type">
                            <template v-slot:trigger>
                              <a
                                :id="c.type"
                                class="editor-actions"
                                :class="{ active: activeAction === c.type }"
                                tabindex="0"
                                href="javascript:;"
                                role="button"
                                :aria-label="c.name"
                                @click="handleActionClick(c)"
                              >
                                <span class="p-4">{{ c.name }}</span>
                              </a>
                            </template>
                            <span>{{ c.name }}</span>
                          </MTooltip>
                        </div>
                        <div class="flex items-center gap-2">
                          <MTooltip v-if="isAddingPrivateConversation">
                            <template v-slot:trigger>
                              <MButton
                                variant="default"
                                class="button-neutral-lighter rounded p-0 private-lock"
                              >
                                <MIcon
                                  name="lock-alt"
                                  size="lg"
                                  class="text-light p-1"
                                />
                              </MButton>
                            </template>
                            {{ $t('not_visible_to_requester') }}
                          </MTooltip>
                          <MTooltip>
                            <template v-slot:trigger>
                              <MButton
                                variant="default"
                                class="button-neutral-lighter rounded p-0 close-icon"
                                @click="resetConversationState"
                              >
                                <MIcon
                                  name="times"
                                  size="lg"
                                  class="text-light p-1"
                                />
                              </MButton>
                            </template>
                            {{ $t('cancel') }}
                          </MTooltip>
                        </div>
                      </div>
                    </template>
                    <template v-else v-slot:editor-actions>
                      <div class="editor-actions-container">
                        <span class="p-2 font-medium text-primary">{{
                          $t(activeAction)
                        }}</span>
                        <div class="flex items-center gap-2">
                          <MTooltip v-if="isAddingPrivateConversation">
                            <template v-slot:trigger>
                              <MButton
                                variant="default"
                                class="button-neutral-lighter rounded p-0 private-lock"
                              >
                                <MIcon
                                  name="lock-alt"
                                  size="lg"
                                  class="text-light p-1"
                                />
                              </MButton>
                            </template>
                            {{ $t('not_visible_to_requester') }}
                          </MTooltip>
                          <MTooltip>
                            <template v-slot:trigger>
                              <MButton
                                variant="default"
                                class="button-neutral-lighter rounded p-0 close-icon"
                                @click="resetConversationState"
                              >
                                <MIcon
                                  name="times"
                                  size="lg"
                                  class="text-light p-1"
                                />
                              </MButton>
                            </template>
                            {{ $t('cancel') }}
                          </MTooltip>
                        </div>
                      </div>
                    </template>
                  </AddBox>
                </MCol>
              </MRow>
            </div>
          </Transition>
        </template>
        <template v-slot:before-list>
          <MRow v-if="diagnosis.text" :gutter="0">
            <MCol>
              <Conversation
                :key="diagnosis.createdAt"
                :disabled="disabled"
                type="diagnosis"
                :text="diagnosis.text"
                :created-at="diagnosis.createdAt"
                :attachments="diagnosis.fileAttachments"
                :owner="diagnosis.owner"
                :use-private-indicator="true"
                is-private
                :can-edit="true"
                :can-delete="true"
                :rich-box-rows="richBoxRows"
                @edit="editDiagnosis"
                @delete="deleteDiagnosis"
              />
            </MCol>
          </MRow>
          <MRow v-if="solution.text" :gutter="0">
            <MCol>
              <Conversation
                :key="solution.createdAt"
                :disabled="disabled"
                type="solution"
                :text="solution.text"
                :attachments="solution.fileAttachments"
                :created-at="solution.createdAt"
                :owner="solution.owner"
                :can-edit="!isPortalLogin"
                :can-delete="!isPortalLogin"
                :use-private-indicator="usePrivateIndicator"
                :rich-box-rows="richBoxRows"
                @edit="editSolution"
                @delete="deleteSolution"
              />
            </MCol>
          </MRow>
          <MRow v-if="conclusion.text" :gutter="0">
            <MCol>
              <Conversation
                :key="conclusion.createdAt"
                :disabled="disabled"
                type="conclusion"
                :text="conclusion.text"
                :attachments="conclusion.fileAttachments"
                :created-at="conclusion.createdAt"
                :owner="conclusion.owner"
                :can-edit="
                  !excludedTypesForCreate ||
                  excludedTypesForCreate.indexOf('conclusion') >= 0
                "
                :can-delete="
                  !excludedTypesForCreate ||
                  excludedTypesForCreate.indexOf('conclusion') >= 0
                "
                :use-private-indicator="usePrivateIndicator"
                :rich-box-rows="richBoxRows"
                @edit="editConclusion"
                @delete="deleteConclusion"
              />
            </MCol>
          </MRow>
        </template>
        <template v-slot:list-items="{ items, remove, update, activateItem }">
          <div class="dynamic-scroller-conversation">
            <DynamicScroller
              :items="items"
              :min-item-size="54"
              :emit-update="true"
              class="scroller"
            >
              <template v-slot="{ item: conversation, index, active }">
                <DynamicScrollerItem
                  :item="conversation"
                  :active="active"
                  :size-dependencies="[conversation.text]"
                  :data-index="index"
                  :data-active="active"
                >
                  <Conversation
                    v-if="conversation"
                    :id="conversation.id"
                    :key="conversation.id"
                    :disabled="disabled"
                    :type="conversation.type"
                    :can-edit="
                      conversation.canEdit ||
                      conversation.conversationState === 'draft'
                    "
                    :can-delete="conversation.canDelete"
                    :is-deletable="isDeletable"
                    :is-private="conversation.isPrivate"
                    :text="conversation.text"
                    :created-at="conversation.createdAt"
                    :attachments="conversation.fileAttachments"
                    :owner="conversation.owner"
                    :rating="conversation.rating"
                    :field-value-details="conversation.fieldValueDetails"
                    :subject="conversation.subject"
                    :resource="resource"
                    :email-ids="conversation.emailIds"
                    :cc-email-ids="conversation.ccEmailIds"
                    :use-private-indicator="usePrivateIndicator"
                    inline-edit
                    :allow-save-in-draft="conversationCanSaveInDraft"
                    :is-draft="conversation.conversationState === 'draft'"
                    :update="update"
                    :rich-box-rows="richBoxRows"
                    :allow-templates="allowTemplates"
                    :allow-knowledges="allowInsertknowledges"
                    :module-name="moduleName"
                    :default-email-send-config-id="defaultOutgoingEmail.id"
                    style="max-height: 9999px; overflow-y: auto"
                    @edit="activateItem(conversation)"
                    @delete="remove(conversation)"
                    @forward="handleForwardConversation"
                    @reply="
                      handleForwardConversation(
                        $event,
                        'technician_to_requester'
                      )
                    "
                  />
                </DynamicScrollerItem>
              </template>
            </DynamicScroller>
          </div>
        </template>
      </FlotoCrudContainer>
    </MCol>
  </MRow>
</template>

<script>
import {
  getDefaultOutgoingEmailApi,
  updateSolutionApi,
  updateConclusionApi,
  updateDiagnosisApi,
  getWorkApi,
  addConversationApi,
  updateConversationApi,
  deleteConversationApi,
  getConversationsApi,
} from './api'
import SortBy from 'lodash/sortBy'
import OrderBy from 'lodash/orderBy'
import { authComputed } from '@state/modules/auth'
import { PreferenceComputed } from '@state/modules/preference'
import Bus from '@utils/emitter'
import { getGlobalUserApi } from '@modules/users/users-api'
import { TechnicianComputed } from '@state/modules/technician'
import Searchbox from './search-box'
import AddBox from './conversation-add-box'
import { ConversationType } from './conversation-types'
import Conversation from './conversation'
import { SupportPortalConfigComputed } from '@state/modules/support-portal-config'
import { taskPermissionsMixin } from '@src/mixins/task-permissions'
export default {
  name: 'ConversationContainer',
  components: { Searchbox, AddBox, Conversation },
  mixins: [taskPermissionsMixin],
  props: {
    fetchFn: { type: Function, default: undefined },
    // eslint-disable-next-line
    searchable: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    hideSearchBar: { type: Boolean, default: false },
    resourceId: { type: Number, required: true },
    resource: {
      type: Object,
      default() {
        return {}
      },
    },
    moduleName: { type: String, required: true },
    allowedTypes: {
      type: Array,
      default() {
        return []
      },
    },
    excludedTypes: {
      type: Array,
      default() {
        return ['requestor_to_technician']
      },
    },
    allowTemplates: { type: Boolean, default: false },
    allowKnowledges: { type: Boolean, default: false },
    usePrivateIndicator: { type: Boolean, default: false },
    richBoxRows: { type: Number, default: undefined },
    stickySearch: { type: Boolean, default: false },
    offsetTop: { type: Number, default: 0 },
    excludedTypesForCreate: { type: Array, default: undefined },
    allowSaveInDraft: { type: Boolean, default: false },
    // eslint-disable-next-line
    canCreate: { type: Boolean, default: true },
    canAddReview: { type: Boolean, default: false },
    applyAddedByFilter: { type: Boolean, default: false },
    // eslint-disable-next-line
    isDeletable: { type: Boolean, default: true },
  },
  data() {
    this.systemUserId = -93102
    let availableTypes = SortBy(
      Object.keys(ConversationType())
        .filter(
          (k) => ConversationType()[k].modules.indexOf(this.moduleName) >= 0
        )
        .map((k) => ConversationType()[k]),
      'order'
    )
    if (this.allowedTypes.length) {
      availableTypes = availableTypes.filter((f) =>
        this.allowedTypes.includes(f.type)
      )
    }
    if (this.excludedTypes.length) {
      availableTypes = availableTypes.filter(
        (f) => !this.excludedTypes.includes(f.type)
      )
    }
    this.systemUserId = -93102
    return {
      isEnabledSearch: false,
      availableTypes,
      currentConversationType: null,
      processing: false,
      solution: {},
      conclusion: {},
      diagnosis: {},
      filters: {},
      searchAffixed: false,
      latestReplay: {},
      forwardDefalutContent: {},
      showNoteAsFirstResponse: false,
      defaultOutgoingEmail: {},
      activeAction: null,
      startedFromInput: false,
      showForwardOption: false,
      showReplyOption: false,
    }
  },
  computed: {
    ...authComputed,
    ...PreferenceComputed,
    ...TechnicianComputed,
    ...SupportPortalConfigComputed,
    filteredCreateOptions() {
      return this.createOptions.filter((opt) =>
        ['solution', 'conclusion', 'diagnosis'].includes(opt.type)
      )
    },
    conversationCanSaveInDraft() {
      if (
        this.currentConversationType &&
        ['solution', 'conclusion', 'diagnosis', 'note'].indexOf(
          this.currentConversationType.type
        ) >= 0
      ) {
        return false
      }
      if (this.allowSaveInDraft) {
        return this.allowConversationSaveInDraft
      }
      return false
    },
    allowSubject() {
      if (
        ['forward_notification'].indexOf(
          (this.currentConversationType || {}).type
        ) >= 0
      ) {
        return true
      }
      return false
    },
    allowEmail() {
      if (
        ['forward_notification', 'technician_to_requester'].indexOf(
          (this.currentConversationType || {}).type
        ) >= 0
      ) {
        return true
      }
      return false
    },
    allowCcEmail() {
      if (
        ['forward_notification', 'technician_to_requester'].indexOf(
          (this.currentConversationType || {}).type
        ) >= 0
      ) {
        return true
      }
      return false
    },
    allowOutgoingEmail() {
      if (
        ['forward_notification', 'technician_to_requester'].indexOf(
          (this.currentConversationType || {}).type
        ) >= 0
      ) {
        return true
      }
      return false
    },
    isAddingPrivateConversation() {
      if (this.currentConversationType) {
        if (
          this.moduleName === this.$constants.REQUEST &&
          ['diagnosis', 'note', 'collaborate'].indexOf(
            (this.currentConversationType || {}).type
          ) >= 0
        ) {
          return true
        }
      }
      return false
    },

    createOptions() {
      return this.availableTypes
        .filter((t) => t.canCreate !== false)
        .filter((c) => {
          const excludedTypes = ['solution', 'conclusion', 'diagnosis']

          if (this.showForwardOption) {
            return true
          }
          if (this.showReplyOption) {
            return true
          }

          if (this.startedFromInput) {
            return !excludedTypes.includes(c.type)
          }

          if (this.isPortalLogin) {
            if (
              ['requestor_to_technician', 'comment_on_approval', 'note']
                .concat(
                  this.moduleName === this.$constants.PROJECT
                    ? ['collaborate']
                    : this.moduleName === this.$constants.TASK
                    ? ['comment_on_task']
                    : []
                )
                .indexOf(c.type) >= 0
            ) {
              return true
            }

            if (c.type === 'review') {
              return this.canAddReview
            }
            return false
          }

          if (c.type === 'review') {
            return this.canAddReview
          }

          if (
            this.excludedTypesForCreate &&
            this.excludedTypesForCreate.indexOf(c.type) === -1
          ) {
            return false
          }
          if (!excludedTypes.includes(c.type)) {
            return true
          }
          return true
        })
    },
    filterTypeOptions() {
      return this.availableTypes.filter(
        (f) => ['solution', 'conclusion', 'diagnosis'].indexOf(f.type) === -1
      )
    },
    currentConversation() {
      if (
        this.currentConversationType &&
        this.currentConversationType.type === 'diagnosis'
      ) {
        return {
          value: this.diagnosis.text,
          fileAttachments: this.diagnosis.fileAttachments,
        }
      }
      if (
        this.currentConversationType &&
        this.currentConversationType.type === 'solution'
      ) {
        return {
          value: this.solution.text,
          fileAttachments: this.solution.fileAttachments,
        }
      }
      if (
        this.currentConversationType &&
        this.currentConversationType.type === 'conclusion'
      ) {
        return {
          value: this.conclusion.text,
          fileAttachments: this.conclusion.fileAttachments,
        }
      }
      return {}
    },
    allowInsertknowledges() {
      if (
        this.currentConversationType &&
        this.currentConversationType.type === 'solution'
      ) {
        return true
      }
      if (
        this.currentConversationType &&
        this.currentConversationType.type === 'conclusion'
      ) {
        return true
      }

      return this.allowKnowledges
    },
  },
  watch: {
    'resource.description': {
      handler(newValue, prevValue) {
        if (newValue !== prevValue) {
          this.prepaireForwardContent()
        }
      },
    },
    'resource.fileAttachments': {
      handler(newValue, prevValue) {
        if (newValue !== prevValue) {
          this.prepaireForwardContent()
        }
      },
    },
  },
  created() {
    if (this.moduleName === this.$constants.REQUEST && !this.isPortalLogin) {
      this.getDefaultOutgoingEmail()
    }
    if (
      this.createOptions.length === 0 ||
      (this.createOptions.length > 0 && this.disabled)
    ) {
      this.isEnabledSearch = true
    }
    if (this.createOptions.length > 0) {
      this.activeAction = this.createOptions[0].type
    }
  },
  mounted() {
    const shouldShowDiagnosis = this.availableTypes.find(
      (t) => ['diagnosis', 'solution', 'conclusion'].indexOf(t.type) >= 0
    )
    if (
      shouldShowDiagnosis &&
      (this.isPortalLogin ? this.allowRequesterToAccessSolution : true)
    ) {
      getWorkApi(this.moduleName, this.resourceId, this.isPortalLogin).then(
        (data) => {
          this.solution = data.solution
          this.conclusion = data.conclusion
          this.diagnosis = data.diagnosis
          this.$emit('work-received', data)
        }
      )
    }
    const availableCommands = this.createOptions
      .filter((c) => c.command)
      .map((c) => c.command)
    const createShortcutHandler = (e, k, command) => {
      const typeToSet = this.createOptions
        .filter((c) => c.command)
        .find((c) => c.command === command)
      if (typeToSet) {
        this.currentConversationType = typeToSet
      }
    }
    Bus.$on(availableCommands, createShortcutHandler)
    this.$off('hook:beforeDestroy', () => {
      Bus.$off(availableCommands, createShortcutHandler)
    })
    this.prepaireForwardContent()
  },
  methods: {
    handleEditConvesation(activateItem, conversation) {
      this.currentConversationType = ConversationType()[conversation.type]
      this.currentConversation = conversation
      activateItem(conversation)
    },
    prepaireLatestReplay() {
      if (this.technicianDetails(this.resource.requesterId)) {
        this.latestReplay = {
          text: this.resource.description,
          createdAt: this.resource.createdAt,
          owner: `${this.technicianDetails(this.resource.requesterId).name} ${
            this.technicianDetails(this.resource.requesterId).email
              ? `(${this.technicianDetails(this.resource.requesterId).email})`
              : ''
          }`,
        }
      } else {
        this.getRequester(this.resource.requesterId).then((user) => {
          this.latestReplay = {
            text: this.resource.description,
            createdAt: this.resource.createdAt,
            owner: `${user.name} ${user.email ? `(${user.email})` : ''}`,
          }
        })
      }
    },
    prepaireForwardContent() {
      if (
        this.resource.description ||
        (this.resource.fileAttachments || []).length
      ) {
        if (this.technicianDetails(this.resource.requesterId)) {
          this.forwardDefalutContent = {
            text: this.resource.description,
            createdAt: this.resource.createdAt,
            owner: `${this.technicianDetails(this.resource.requesterId).name} ${
              this.technicianDetails(this.resource.requesterId).email
                ? `(${this.technicianDetails(this.resource.requesterId).email})`
                : ''
            }`,
            attachments: this.resource.fileAttachments,
          }
        } else {
          this.getRequester(this.resource.requesterId).then((user) => {
            this.forwardDefalutContent = {
              text: this.resource.description,
              createdAt: this.resource.createdAt,
              owner: `${user.name} ${user.email ? `(${user.email})` : ''}`,
              attachments: this.resource.fileAttachments,
            }
          })
        }
      }
    },
    technicianDetails(id) {
      const techUser = this.technician(id)
      return techUser.id ? this.technician(id) : undefined
    },
    getRequester(userId) {
      if (userId) {
        const user = this.technician(userId)
        if ((user && user.id) || userId === this.systemUserId) {
          return Promise.resolve({ name: user.name })
        } else {
          return getGlobalUserApi(userId, { archived: true }).then((data) => {
            return data
          })
        }
      }
    },
    handleForwardConversation(data, type = 'forward_notification') {
      if (type === 'forward_notification') {
        this.showForwardOption = true
      } else if (type === 'technician_to_requester') {
        this.showReplyOption = true
      }
      const forward = this.createOptions.find((c) => c.type === type)
      if (forward) {
        this.setAddConversationType(forward)
        let content = {}
        if (this.technicianDetails(data.owner)) {
          content = {
            text: data.text,
            createdAt: data.createdAt,
            owner: `${(this.technicianDetails(data.owner) || {}).name} ${
              (this.technicianDetails(data.owner) || {}).email
                ? `(${this.technicianDetails(data.owner).email})`
                : ''
            }`,
            attachments: data.attachments,
          }
        } else if (data.owner === this.systemUserId) {
          content = {
            text: data.text,
            createdAt: data.createdAt,
            owner: this.$tc('system'),
            attachments: data.attachments,
          }
        } else {
          this.getRequester(data.owner).then((user) => {
            content = {
              text: data.text,
              createdAt: data.createdAt,
              owner: `${user.name} ${user.email ? `(${user.email})` : ''}`,
              attachments: data.attachments,
            }
          })
        }
        setTimeout(() => {
          if (type === 'forward_notification') {
            Bus.$emit('app:conversation:forward:content', {
              ...content,
            })
          }
          if (type === 'technician_to_requester') {
            Bus.$emit('app:conversation:reply:content', {
              ...content,
              emailIds: data.emailIds,
              ccEmailIds: data.ccEmailIds,
            })
          }
          this.showForwardOption = false
          this.showReplyOption = false
        }, 500)
      }
    },
    setAddConversationType(c) {
      if (!this.canCreate) {
        return
      }

      this.currentConversationType = c
      this.activeAction = c.type
      this.$nextTick(() => {
        this.ensureAddBoxVisible()
      })
    },
    getNoteAsFirstResponse(items) {
      const publisedItems = items.filter(
        (i) => i.conversationState === 'published'
      )
      const ifAnyTechnicianReplayAvailable =
        (
          publisedItems.filter((i) => i.type === 'technician_to_requester') ||
          []
        ).length > 0
      const ifAnyNoteAsFirstResponseAvailable =
        (publisedItems.filter((i) => i.noteAsFirstResponse) || []).length > 0
      this.showNoteAsFirstResponse = !(
        ifAnyTechnicianReplayAvailable || ifAnyNoteAsFirstResponseAvailable
      )
    },
    getLatestReplayFromList(items) {
      const replayTypes = this.availableTypes.filter(
        (a) =>
          ['technician_to_requester', 'requestor_to_technician'].indexOf(
            a.type
          ) >= 0
      )
      if (replayTypes.length > 0) {
        const replayList = (items || []).filter(
          (i) =>
            ['technician_to_requester', 'requestor_to_technician'].indexOf(
              i.type
            ) >= 0 && i.conversationState === 'published'
        )
        const latestReplay =
          OrderBy(replayList || [], ['createdAt'], ['desc']) || []
        if ((latestReplay || []).length) {
          const item = latestReplay[0]
          // if owner is requester then fetch owner name from id
          if (this.technicianDetails(item.owner)) {
            this.latestReplay = {
              text: item.text,
              createdAt: item.createdAt,
              owner: `${this.technicianDetails(item.owner).name} ${
                this.technicianDetails(item.owner).email
                  ? `(${this.technicianDetails(item.owner).email})`
                  : ''
              }`,
            }
          } else if (item.owner === this.systemUserId) {
            this.latestReplay = {
              text: item.text,
              createdAt: item.createdAt,
              owner: this.$tc('system'),
            }
          } else {
            this.getRequester(item.owner).then((user) => {
              this.latestReplay = {
                text: item.text,
                createdAt: item.createdAt,
                owner: `${user.name} ${user.email ? `(${user.email})` : ''}`,
              }
            })
          }
        } else if (this.resource.description) {
          this.prepaireLatestReplay()
        }
      }
    },
    getConversations(limit, offset, sortBy) {
      if (this.fetchFn) {
        return this.fetchFn(limit, offset, sortBy, this.filters).then(
          (data) => {
            if (
              (this.tenantPrefrences.populateLastConversationInReply || {})
                .value === true
            ) {
              this.getLatestReplayFromList(data.items)
            }
            this.getNoteAsFirstResponse(data.items)
            return data
          }
        )
      }
      return getConversationsApi(
        this.moduleName,
        this.resourceId,
        { ...this.filters, sortBy },
        limit,
        offset
      ).then((data) => {
        if (
          (this.tenantPrefrences.populateLastConversationInReply || {})
            .value === true
        ) {
          this.getLatestReplayFromList(data.items)
        }
        this.getNoteAsFirstResponse(data.items)
        return data
      })
    },
    addConversation(data) {
      this.processing = true
      return addConversationApi(this.moduleName, this.resourceId, {
        ...data,
        type: this.currentConversationType.type,
        name: this.currentConversationType.name,
      })
        .then((data) => {
          this.processing = false
          this.resetConversationState()
          this.$emit('refresh')
          // @TODO kiran
          let updatedData = data
          if (Object.keys(this.filters).length && data.type) {
            if (data.type === this.filters.conversationType) {
              updatedData = data
            } else {
              updatedData = null
            }
          }
          if (
            ['technician_to_requester', 'requestor_to_technician'].indexOf(
              data.type
            ) >= 0 ||
            data.noteAsFirstResponse ||
            this.searchable
          ) {
            this.refresh()
          }
          return updatedData
        })
        .finally(() => (this.processing = false))
    },
    updateConversation(data, saveAndAddToKnowledge) {
      this.processing = true
      let updateFn, isSolution
      if (
        this.currentConversationType &&
        ['solution', 'conclusion', 'diagnosis'].indexOf(
          this.currentConversationType.type
        ) >= 0
      ) {
        if (this.currentConversationType.type === 'solution') {
          updateFn = updateSolutionApi
          if (data.text) {
            isSolution = true
          }
        }
        if (this.currentConversationType.type === 'conclusion') {
          updateFn = updateConclusionApi
        }
        if (this.currentConversationType.type === 'diagnosis') {
          updateFn = updateDiagnosisApi
        }
        if (!updateFn) {
          this.processing = false
        }
        return updateFn(this.moduleName, this.resourceId, data)
          .then((response) => {
            this.processing = false
            this.resetConversationState()
            this.solution = response.solution
            this.conclusion = response.conclusion
            this.diagnosis = response.diagnosis
            this.$emit('work-received', response)
            this.$emit('refresh')
            if (isSolution) {
              this.$emit(
                'solution-added',
                saveAndAddToKnowledge ? data : undefined
              )
            }
          })
          .catch(() => (this.processing = false))
          .finally(() => (this.processing = false))
      } else {
        return updateConversationApi(this.moduleName, this.resourceId, data)
          .then((response) => {
            this.resetConversationState()
            return response
          })
          .finally(() => (this.processing = false))
      }
    },
    editDiagnosis() {
      this.currentConversationType = ConversationType().diagnosis
      this.activeAction = this.currentConversationType.type
    },
    editSolution() {
      this.currentConversationType = ConversationType().solution
      this.activeAction = this.currentConversationType.type
    },
    editConclusion() {
      this.currentConversationType = ConversationType().conclusion
      this.activeAction = this.currentConversationType.type
    },
    deleteSolution() {
      updateSolutionApi(this.moduleName, this.resourceId, {
        text: null,
        fileAttachments: [],
      })
        .then(({ solution }) => {
          this.$emit('refresh')
          return (this.solution = solution)
        })
        .finally(() => (this.processing = false))
    },
    deleteConclusion() {
      updateConclusionApi(this.moduleName, this.resourceId, {
        text: null,
        fileAttachments: [],
      })
        .then(({ conclusion }) => {
          this.$emit('refresh')
          return (this.conclusion = conclusion)
        })
        .finally(() => (this.processing = false))
    },
    deleteDiagnosis() {
      updateDiagnosisApi(this.moduleName, this.resourceId, {
        text: null,
        fileAttachments: [],
      })
        .then(({ diagnosis }) => {
          this.$emit('refresh')
          return (this.diagnosis = diagnosis)
        })
        .finally(() => (this.processing = false))
    },
    deleteConversation(data) {
      return deleteConversationApi(data).then(() => this.$emit('refresh'))
    },
    refresh() {
      this.$refs.list.refresh()
    },
    getDefaultOutgoingEmail() {
      return getDefaultOutgoingEmailApi(this.moduleName, this.resourceId).then(
        (data) => {
          this.defaultOutgoingEmail = data
        }
      )
    },
    handleActionClick(c) {
      this.activeAction = c.type
      this.setAddConversationType(c)
    },
    handleFocus() {
      this.startedFromInput = true
      if (this.createOptions && this.createOptions.length > 0) {
        const firstOption = this.createOptions[0]
        this.activeAction = firstOption.type
        this.$nextTick(() => {
          this.setAddConversationType(firstOption)
          this.$nextTick(() => this.ensureAddBoxVisible())
        })
      }
    },
    resetConversationState() {
      this.startedFromInput = false
      this.currentConversationType = null
      this.activeAction =
        this.createOptions.length > 0 ? this.createOptions[0].type : null
    },
    ensureAddBoxVisible() {
      try {
        const addBox = this.$refs.addConversationContainerRef
        if (!addBox) return
        const scroller = this.getInnerScrollEl()
        if (!scroller) return
        const scrollerRect = scroller.getBoundingClientRect()
        const addBoxRect = addBox.getBoundingClientRect()
        const offsetTop = addBoxRect.top - scrollerRect.top
        const stickyOffset = 50 // reserve space for sticky headers/tabs
        scroller.scrollTop += offsetTop - stickyOffset
      } catch (e) {}
    },
    getInnerScrollEl() {
      const root =
        this.$el && this.$el.closest && this.$el.closest('.detail-layout')
      return (
        (root &&
          root.querySelector &&
          (root.querySelector('.main-detail-scroll .__panel') ||
            root.querySelector('.main-detail-scroll'))) ||
        document.querySelector('.detail-layout .main-detail-scroll .__panel') ||
        document.querySelector('.detail-layout .main-detail-scroll') ||
        null
      )
    },
  },
}
</script>

<style lang="less" scoped>
.action {
  position: relative;
  width: 30px;
  height: 30px;

  .check-badge {
    position: absolute;
    top: -2px;
    right: -4px;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 13px;
    height: 13px;
    background-color: var(--success-button-bg);
    border-radius: 50%;
  }

  &:not(.active) {
    background-color: var(--page-background-color);
    border: 1px solid var(--neutral-lighter-button-bg);

    &:hover {
      background-color: var(--neutral-lighter-button-hover-bg);
      border: 1px solid var(--neutral-lighter-button-hover-bg);
    }

    &:focus-visible {
      border: 1px solid var(--neutral-lighter-button-hover-bg);
      outline: 1px solid var(--primary-button-hover-bg);
    }
  }

  &.active {
    color: var(--white-regular);
  }

  @apply rounded p-3 inline-flex items-center justify-center;
}

.editor-actions-container {
  display: flex;
  gap: 2px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--neutral-lighter-button-hover-bg);

  .close-icon {
    display: flex;
    align-items: center;
    margin-right: 6px;
    margin-left: auto;

    // padding: 6px 8px;
  }

  .private-lock {
    margin-right: 6px;
  }
}
</style>
<style lang="less">
.dynamic-scroller-conversation {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 200px);
  overflow: hidden;
}

.editor-actions {
  position: relative;
  display: inline-block;
  padding: 8px 0;
  color: var(--text-color-common-secondary);
  text-decoration: none;
  cursor: pointer;
  background-color: transparent;
  border: none;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    border-radius: 4px;
    outline: 2px solid var(--primary-button-hover-bg);
    outline-offset: -2px;
  }

  &:hover {
    color: var(--primary);
  }

  &.active {
    color: var(--primary);

    &::after {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 3px;
      content: '';
      background-color: var(--primary-button-hover-bg);
    }
  }
}

.text-success {
  color: var(--success-button-bg) !important;
}

.text-light {
  color: var(--neutral-regular);
}
</style>
